import { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Search,
  Plus,
  BookOpen,
  ChefHat,
  Printer,
  Edit2,
  Trash2,
  X,
  Save,
  Scale,
  Calculator,
  AlertTriangle,
  Globe,
  Filter,
  FileText,
  FileCode,
  Copy,
  ToggleLeft,
  ToggleRight
} from 'lucide-react'
import { groupChefRecipes } from '../lib/kitchenWorkflow'
import { useApp } from '../App'
import { recipesAPI, exportAPI, menusAPI, orderingAPI, formatQuantity, getCategoryColor } from '../lib/api'
import { tr, trIngredient, trLabel } from '../lib/translations'
import { useToastActions } from '../components/Toast'
import { loadMenuRecipeScales } from '../lib/menuRecipeScales'
import { buildHavnContext, mdTable, downloadMD, buildFilename, fmtNum, slugify } from '../lib/mdExport'

// ══════════════════════════════════════════════════════════════════════════════
// MARKDOWN EXPORT HELPERS — LLM-optimized recipe exports
// ══════════════════════════════════════════════════════════════════════════════

function renderRecipeMDSection(recipe, scaledData = null) {
  const isScaled = !!scaledData
  if (!scaledData) return `## ${recipe.name}\n\n_Menu batch unavailable. No production quantities exported._\n\n`
  const batchSize = scaledData.scaled_batch_size
  const batchUnit = scaledData.scaled_batch_unit
  const ingredients = scaledData.ingredients || []

  let out = `## ${recipe.name}\n\n`
  out += `- **Category:** ${recipe.category || 'uncategorized'}\n`
  out += `- **Batch size:** ${fmtNum(batchSize)} ${batchUnit}\n`
  if (recipe.allergens?.length) {
    out += `- **Allergens:** ${recipe.allergens.join(', ')}\n`
  } else {
    out += `- **Allergens:** none\n`
  }
  out += `\n`

  if (ingredients.length) {
    out += `### Ingredients\n\n`
    out += mdTable(
      ['Ingredient', 'Quantity', 'Unit'],
      ingredients.map((ing) => [ing.ingredient_name || '', fmtNum(ing.quantity), ing.unit || '']),
    )
    out += `\n\n`
  } else {
    out += `_No ingredients listed._\n\n`
  }

  if (recipe.prep_instructions) {
    out += `### Prep Instructions\n\n${recipe.prep_instructions.trim()}\n\n`
  }
  if (recipe.cook_instructions) {
    out += `### Cook Instructions\n\n${recipe.cook_instructions.trim()}\n\n`
  }
  if (recipe.chef_notes) {
    out += `### Chef Notes\n\n${recipe.chef_notes.trim()}\n\n`
  }
  return out
}

function buildSingleRecipeMD(recipe, scaledData = null, referenceMode = false) {
  let md = buildHavnContext({
    docType: 'Recipe Card',
    docPurpose: 'Single recipe — ingredients, batch size, prep/cook instructions, chef notes, allergens.',
  })
  md += `# Recipe: ${recipe.name}\n\n`
  if (referenceMode) md += '> Reference batch — not the current menu production target.\n\n'
  md += renderRecipeMDSection(recipe, scaledData)
  return md
}

function buildAllRecipesMD(recipes, menuName = null, cityId = null, scaledMap = null) {
  const isScaled = !!scaledMap
  let md = buildHavnContext({
    docType: isScaled ? 'Recipe Book (scaled to this week\'s menu quantities)' : 'Recipe Book (all recipes for menu)',
    docPurpose: isScaled
      ? 'Every recipe linked to this week\'s menu, with batch sizes and ingredient amounts SCALED to the quantities the menu needs — these are NOT base recipe amounts. Each section is one recipe.'
      : 'Every recipe linked to the dishes on this week\'s menu. Each section is one recipe with ingredients, batch size, instructions, and allergens.',
    city: cityId,
    menu: menuName,
  })
  md += `# Recipes${menuName ? ` — ${menuName}` : ''}${isScaled ? ' (scaled to menu)' : ''}\n\n`
  md += `Total recipes: ${recipes.length}\n\n`

  // Group by category for easier scanning
  const byCategory = {}
  recipes.forEach((r) => {
    const cat = r.category || 'uncategorized'
    if (!byCategory[cat]) byCategory[cat] = []
    byCategory[cat].push(r)
  })

  const categoryOrder = ['protein', 'sauce', 'vegetable', 'grain', 'base', 'seasoning', 'uncategorized']
  const orderedCats = [
    ...categoryOrder.filter((c) => byCategory[c]),
    ...Object.keys(byCategory).filter((c) => !categoryOrder.includes(c)),
  ]

  orderedCats.forEach((cat) => {
    md += `# ${cat.charAt(0).toUpperCase() + cat.slice(1)} (${byCategory[cat].length})\n\n`
    byCategory[cat]
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach((recipe) => {
        md += renderRecipeMDSection(recipe, scaledMap ? scaledMap[recipe.db_id] : null)
      })
  })

  return md
}

// ══════════════════════════════════════════════════════════════════════════════
// RECIPE CARD COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

function RecipeCard({ recipe, scaledData, scalingComplete, scalingError, onEdit, onDelete, onView, onDuplicate, chefMode = false, referenceMode = false }) {
  const { isSpanish, translations } = useApp()
  const categoryColors = {
    protein: 'bg-red-100 text-red-700 border-red-200',
    sauce: 'bg-purple-100 text-purple-700 border-purple-200',
    vegetable: 'bg-green-100 text-green-700 border-green-200',
    grain: 'bg-amber-100 text-amber-700 border-amber-200',
    base: 'bg-blue-100 text-blue-700 border-blue-200',
    seasoning: 'bg-orange-100 text-orange-700 border-orange-200',
  }

  const colorClass = categoryColors[recipe.category] || categoryColors.base

  // Operational recipe cards only show this menu's production batch. The base
  // recipe remains available inside Edit as the scaling source, never as a
  // competing set of kitchen quantities.
  const displayBatchSize = scaledData?.scaled_batch_size ?? (referenceMode ? recipe.base_batch_size : undefined)
  const displayBatchUnit = scaledData?.scaled_batch_unit ?? (referenceMode ? recipe.base_batch_unit : undefined)
  const displayIngredients = scaledData?.ingredients || (referenceMode ? recipe.ingredients : [])
  const isScaled = !!scaledData
  const displayName = tr(isSpanish, translations, 'recipes', recipe.db_id || recipe.id, 'name', recipe.name)
  const displayChefNotes = tr(isSpanish, translations, 'recipes', recipe.db_id || recipe.id, 'chef_notes', recipe.chef_notes)
  const displayAllergens = isSpanish
    ? (translations?.recipes?.[String(recipe.db_id || recipe.id)]?.allergens || recipe.allergens)
    : recipe.allergens

  return (
    <div
      className={`bg-white rounded-xl border hover:shadow-soft transition-all group cursor-pointer ${
        isScaled ? 'border-havn-green-300 ring-1 ring-havn-green-100' : 'border-havn-cream-300 hover:border-havn-green-300'
      }`}
      onClick={() => onView(recipe)}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs rounded-full border ${colorClass}`}>
              {recipe.category}
            </span>
            {isScaled && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-havn-green-100 text-havn-green-700 border border-havn-green-200">
                <Scale className="w-3 h-3 inline mr-1" />
                Scaled
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!chefMode && <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(recipe) }}
              className="p-1.5 text-havn-gray-400 hover:text-havn-green-700 hover:bg-havn-cream-200 rounded transition-colors"
              title="Duplicate recipe"
            >
              <Copy className="w-4 h-4" />
            </button>}
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(recipe) }}
              className="p-1.5 text-havn-gray-400 hover:text-havn-green-700 hover:bg-havn-cream-200 rounded transition-colors"
              title="Edit recipe"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {!chefMode && <button
              onClick={(e) => { e.stopPropagation(); onDelete(recipe) }}
              className="p-1.5 text-havn-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete recipe"
            >
              <Trash2 className="w-4 h-4" />
            </button>}
          </div>
        </div>

        <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-2 group-hover:text-havn-green-700 transition-colors">
          {displayName}
        </h3>

        <div className="flex items-center gap-4 text-sm text-havn-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <Scale className="w-4 h-4" />
            <span className={isScaled ? 'text-havn-green-700 font-medium' : ''}>
              {referenceMode ? `Reference: ${formatQuantity(displayBatchSize)} ${displayBatchUnit}` : isScaled ? `${formatQuantity(displayBatchSize)} ${displayBatchUnit}` : (scalingError ? 'Menu batch unavailable' : scalingComplete ? 'Not used this menu' : 'Calculating menu batch…')}
            </span>
            {isScaled && scaledData?.scale_factor && (
              <span className="text-xs text-havn-gray-400">
                ({scaledData.scale_factor}x)
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            <span>{displayIngredients?.length || 0} ingredients</span>
          </div>
        </div>

        {/* Key Ingredients Preview - show scaled quantities if available */}
        <div className="flex flex-wrap gap-1.5">
          {displayIngredients?.slice(0, 4).map((ing, i) => (
            <span key={i} className={`px-2 py-0.5 text-xs rounded-full ${
              isScaled ? 'bg-havn-green-50 text-havn-green-700' : 'bg-havn-cream-200 text-havn-gray-600'
            }`}>
              {trIngredient(isSpanish, translations, recipe.db_id || recipe.id, ing.ingredient_name, ing.ingredient_name)}
              {isScaled && <span className="ml-1 font-medium">{formatQuantity(ing.quantity)}</span>}
            </span>
          ))}
          {displayIngredients?.length > 4 && (
            <span className="px-2 py-0.5 bg-havn-cream-200 text-havn-gray-400 text-xs rounded-full">
              +{displayIngredients.length - 4} more
            </span>
          )}
        </div>

        {/* Allergens */}
        {displayAllergens?.length > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-havn-cream-200">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-havn-gray-500">
              {trLabel(isSpanish, translations, 'Contains')}: {displayAllergens.join(', ')}
            </span>
          </div>
        )}

        {/* Chef Notes Preview */}
        {displayChefNotes && (
          <div className="mt-3 pt-3 border-t border-havn-cream-200">
            <div className="flex items-start gap-2">
              <ChefHat className="w-4 h-4 text-havn-brass-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-havn-gray-500 italic line-clamp-2">
                {displayChefNotes}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// RECIPE DETAIL MODAL
// ══════════════════════════════════════════════════════════════════════════════

function RecipeDetailModal({ recipe, scaledData: menuScaledData, referenceMode = false, scalingComplete, scalingError, onClose, onPrint, onEdit }) {
  const { isSpanish, translations, translationsLoading } = useApp()
  const [isEditing, setIsEditing] = useState(false)
  const [referenceScaled, setReferenceScaled] = useState(null)
  const [referenceTarget, setReferenceTarget] = useState(recipe.base_batch_size)
  const [referenceBusy, setReferenceBusy] = useState(false)
  const [referenceError, setReferenceError] = useState('')
  const scaledData = referenceMode ? referenceScaled : menuScaledData
  async function scaleReference() {
    if (!(Number(referenceTarget) > 0)) { setReferenceError('Enter a positive batch size.'); return }
    setReferenceBusy(true); setReferenceError('')
    try { setReferenceScaled(await recipesAPI.scale(recipe.db_id ?? recipe.id, Number(referenceTarget))) }
    catch (err) { setReferenceError('Could not scale the reference batch. The displayed batch has not changed.') }
    finally { setReferenceBusy(false) }
  }
  const [editedRecipe, setEditedRecipe] = useState(null)
  const [saving, setSaving] = useState(false)
  const { success, error } = useToastActions()

  // Determine if we're showing scaled view
  const isScaled = !!scaledData
  const displayBatchSize = scaledData?.scaled_batch_size ?? (referenceMode ? recipe.base_batch_size : undefined)
  const displayBatchUnit = scaledData?.scaled_batch_unit ?? (referenceMode ? recipe.base_batch_unit : undefined)
  const displayIngredients = scaledData?.ingredients || (referenceMode ? recipe.ingredients : [])

  // Initialize edited recipe when entering edit mode
  useEffect(() => {
    if (isEditing && !editedRecipe) {
      setEditedRecipe({
        name: recipe.name || '',
        ingredients: recipe.ingredients?.map(ing => ({ ...ing })) || [],
        prep_instructions: recipe.prep_instructions || '',
        cook_instructions: recipe.cook_instructions || '',
        chef_notes: recipe.chef_notes || '',
        base_batch_size: recipe.base_batch_size,
        base_batch_unit: recipe.base_batch_unit
      })
    }
  }, [isEditing, recipe])

  function handleIngredientChange(index, field, value) {
    setEditedRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) =>
        i === index ? { ...ing, [field]: field === 'quantity' ? parseFloat(value) || 0 : value } : ing
      )
    }))
  }

  function handleInstructionChange(field, value) {
    setEditedRecipe(prev => ({ ...prev, [field]: value }))
  }

  function addIngredient() {
    setEditedRecipe(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { ingredient_name: '', quantity: 0, unit: 'lbs' }]
    }))
  }

  function removeIngredient(index) {
    setEditedRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }))
  }

  async function handleSaveChanges() {
    try {
      setSaving(true)
      await recipesAPI.update(recipe.db_id ?? recipe.id, {
        name: editedRecipe.name,
        ingredients: editedRecipe.ingredients,
        prep_instructions: editedRecipe.prep_instructions,
        cook_instructions: editedRecipe.cook_instructions,
        chef_notes: editedRecipe.chef_notes,
        base_batch_size: editedRecipe.base_batch_size,
        base_batch_unit: editedRecipe.base_batch_unit
      })
      success('Recipe updated successfully')
      setIsEditing(false)
      if (onEdit) onEdit() // Refresh the recipes list
    } catch (err) {
      error('Failed to save changes: ' + (err.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  function cancelEditing() {
    setIsEditing(false)
    setEditedRecipe(null)
  }

  // Translated display values come from the shared translation cache populated
  // by App.jsx's prefetch effect. Falls back to English when cache miss.
  const displayRecipe = isSpanish ? {
    ...recipe,
    name: tr(isSpanish, translations, 'recipes', recipe.db_id || recipe.id, 'name', recipe.name),
    chef_notes: tr(isSpanish, translations, 'recipes', recipe.db_id || recipe.id, 'chef_notes', recipe.chef_notes),
    prep_instructions: tr(isSpanish, translations, 'recipes', recipe.db_id || recipe.id, 'prep_instructions', recipe.prep_instructions),
    cook_instructions: tr(isSpanish, translations, 'recipes', recipe.db_id || recipe.id, 'cook_instructions', recipe.cook_instructions),
  } : recipe
  const translating = translationsLoading

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="modal-header">
          <div className="flex-1">
            {isEditing ? (
              <input
                type="text"
                value={editedRecipe?.name || ''}
                onChange={(e) => setEditedRecipe(prev => ({ ...prev, name: e.target.value }))}
                className="font-display text-xl font-semibold text-havn-green-900 bg-transparent border-b-2 border-havn-brass-400 focus:border-havn-green-700 outline-none w-full"
                placeholder="Recipe name"
              />
            ) : (
              <h3 className="font-display text-xl font-semibold text-havn-green-900">
                {displayRecipe.name}
                {translating && <span className="ml-2 text-sm text-havn-gray-400">(Translating...)</span>}
                {isScaled && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-havn-green-100 text-havn-green-700 border border-havn-green-200">
                    <Scale className="w-3 h-3 inline mr-1" />
                    Scaled
                  </span>
                )}
              </h3>
            )}
            {isEditing ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-havn-gray-500">Base batch:</span>
                <input
                  type="number"
                  value={editedRecipe?.base_batch_size || ''}
                  onChange={(e) => setEditedRecipe(prev => ({ ...prev, base_batch_size: parseFloat(e.target.value) || 0 }))}
                  className="text-sm border border-havn-gray-200 rounded px-2 py-1 w-20 focus:border-havn-green-700 outline-none"
                />
                <select
                  value={editedRecipe?.base_batch_unit || 'lbs'}
                  onChange={(e) => setEditedRecipe(prev => ({ ...prev, base_batch_unit: e.target.value }))}
                  className="text-sm border border-havn-gray-200 rounded px-2 py-1 w-24 focus:border-havn-green-700 outline-none"
                >
                  <option value="lbs">lbs</option>
                  <option value="oz">oz</option>
                  <option value="cups">cups</option>
                  <option value="each">each</option>
                  <option value="liters">liters</option>
                  <option value="gallons">gallons</option>
                  <option value="ml">ml</option>
                  <option value="tbsp">tbsp</option>
                  <option value="tsp">tsp</option>
                </select>
              </div>
            ) : (
              <p className="text-sm text-havn-gray-500 mt-1">
                {referenceMode ? <><strong>Reference batch: {formatQuantity(displayBatchSize)} {displayBatchUnit}</strong> · Not this menu's production target</> : isScaled
                  ? <><span className="text-havn-green-700 font-medium">Menu batch: {formatQuantity(displayBatchSize)} {displayBatchUnit}</span><span className="text-havn-gray-400 ml-2">({scaledData.scale_factor}x)</span></>
                  : <>{scalingError ? 'Menu batch unavailable — close this sheet and retry' : scalingComplete ? 'Not used by the current menu' : "Calculating this menu's batch…"}</>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={cancelEditing} className="btn-secondary text-sm">
                  Cancel
                </button>
                <button onClick={handleSaveChanges} disabled={saving} className="btn-primary text-sm">
                  {saving ? <span className="spinner w-4 h-4" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="btn-secondary text-sm">
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                {(isScaled || referenceMode) && <button onClick={() => onPrint(referenceMode ? displayBatchSize : null)} className="btn-secondary text-sm">
                  <Printer className="w-4 h-4" />
                  {referenceMode ? 'Print reference' : 'Print'}
                </button>}
                <button
                  disabled={!isScaled && !referenceMode}
                  onClick={() => {
                    const md = buildSingleRecipeMD(recipe, scaledData || (referenceMode ? { scaled_batch_size: recipe.base_batch_size, scaled_batch_unit: recipe.base_batch_unit, ingredients: recipe.ingredients } : null), referenceMode)
                    downloadMD(buildFilename(`recipe-${slugify(recipe.name)}`), md)
                    success('Recipe exported for AI')
                  }}
                  className="btn-secondary text-sm"
                  title="Download as markdown for feeding into an LLM"
                >
                  <FileCode className="w-4 h-4" />
                  Export for AI
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="modal-body flex-1 overflow-y-auto space-y-6">
          {/* Ingredients */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-havn-charcoal">{trLabel(isSpanish, translations, 'Ingredients')}</h4>
              {isEditing && (
                <button
                  type="button"
                  onClick={addIngredient}
                  className="text-sm text-havn-green-700 hover:text-havn-green-600 font-medium"
                >
                  + Add Ingredient
                </button>
              )}
            </div>
            <div className="bg-havn-cream-50 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-havn-cream-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Ingredient</th>
                    <th className="px-4 py-2 text-right">Quantity</th>
                    <th className="px-4 py-2 text-left">Unit</th>
                    {isEditing && <th className="px-2 py-2 w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {(isEditing ? editedRecipe?.ingredients : displayIngredients)?.map((ing, i) => (
                    <tr key={i} className={`border-t border-havn-cream-200 ${isScaled && !isEditing ? 'bg-havn-green-50/30' : ''}`}>
                      {isEditing ? (
                        <>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={ing.ingredient_name}
                              onChange={(e) => handleIngredientChange(i, 'ingredient_name', e.target.value)}
                              className="input text-sm w-full"
                              placeholder="Ingredient name"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              value={ing.quantity}
                              onChange={(e) => handleIngredientChange(i, 'quantity', e.target.value)}
                              className="input text-sm w-24 text-right"
                              step="1"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={ing.unit}
                              onChange={(e) => handleIngredientChange(i, 'unit', e.target.value)}
                              className="input text-sm w-24"
                            >
                              <option value="lbs">lbs</option>
                              <option value="oz">oz</option>
                              <option value="cups">cups</option>
                              <option value="tbsp">tbsp</option>
                              <option value="tsp">tsp</option>
                              <option value="each">each</option>
                              <option value="liters">liters</option>
                              <option value="gallons">gallons</option>
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              onClick={() => removeIngredient(i)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-medium">{trIngredient(isSpanish, translations, recipe.db_id || recipe.id, ing.ingredient_name, ing.ingredient_name)}</td>
                          <td className={`px-4 py-2 text-right tabular-nums ${isScaled ? 'text-havn-green-700 font-semibold' : ''}`}>
                            {formatQuantity(ing.quantity)}
                          </td>
                          <td className="px-4 py-2 text-havn-gray-500">{ing.unit}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {referenceMode && !isEditing && <div className="p-3 border rounded-lg bg-havn-cream-50">
            <label className="text-sm">Reference / test batch ({recipe.base_batch_unit})<input aria-label="Reference batch size" type="number" min="0.001" step="any" value={referenceTarget} onChange={(event) => setReferenceTarget(event.target.value)} className="input ml-3 w-28" /></label>
            <button className="btn-secondary ml-3" disabled={referenceBusy} onClick={scaleReference}>{referenceBusy ? 'Scaling…' : 'Scale reference'}</button>
            {referenceError && <p role="alert" className="text-red-700 mt-2">{referenceError}</p>}
          </div>}

          {/* Prep Instructions */}
          <div>
            <h4 className="font-semibold text-havn-charcoal mb-2">{trLabel(isSpanish, translations, 'Prep Instructions')}</h4>
            {isEditing ? (
              <textarea
                value={editedRecipe?.prep_instructions || ''}
                onChange={(e) => handleInstructionChange('prep_instructions', e.target.value)}
                className="input w-full min-h-[120px] text-sm"
                placeholder="Enter prep instructions..."
              />
            ) : displayRecipe.prep_instructions ? (
              <p className="text-sm text-havn-gray-600 whitespace-pre-wrap bg-havn-cream-50 rounded-lg p-4">
                {displayRecipe.prep_instructions}
              </p>
            ) : (
              <p className="text-sm text-havn-gray-400 italic bg-havn-cream-50 rounded-lg p-4">
                No prep instructions
              </p>
            )}
          </div>

          {/* Cook Instructions */}
          <div>
            <h4 className="font-semibold text-havn-charcoal mb-2">{trLabel(isSpanish, translations, 'Cook Instructions')}</h4>
            {isEditing ? (
              <textarea
                value={editedRecipe?.cook_instructions || ''}
                onChange={(e) => handleInstructionChange('cook_instructions', e.target.value)}
                className="input w-full min-h-[120px] text-sm"
                placeholder="Enter cooking instructions..."
              />
            ) : displayRecipe.cook_instructions ? (
              <p className="text-sm text-havn-gray-600 whitespace-pre-wrap bg-havn-cream-50 rounded-lg p-4">
                {displayRecipe.cook_instructions}
              </p>
            ) : (
              <p className="text-sm text-havn-gray-400 italic bg-havn-cream-50 rounded-lg p-4">
                No cook instructions
              </p>
            )}
          </div>

          {/* Chef Notes */}
          <div className="p-4 bg-havn-brass-50 rounded-lg">
            <div className="flex items-start gap-3">
              <ChefHat className="w-5 h-5 text-havn-brass-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h5 className="font-semibold text-havn-charcoal text-sm mb-1">{trLabel(isSpanish, translations, 'Chef Notes')}</h5>
                {isEditing ? (
                  <textarea
                    value={editedRecipe?.chef_notes || ''}
                    onChange={(e) => handleInstructionChange('chef_notes', e.target.value)}
                    className="input w-full min-h-[80px] text-sm"
                    placeholder="Tips for execution, variations, etc..."
                  />
                ) : displayRecipe.chef_notes ? (
                  <p className="text-sm text-havn-gray-600">{displayRecipe.chef_notes}</p>
                ) : (
                  <p className="text-sm text-havn-gray-400 italic">No chef notes</p>
                )}
              </div>
            </div>
          </div>

          {/* Allergens */}
          {(() => {
            const modalAllergens = isSpanish
              ? (translations?.recipes?.[String(recipe.db_id || recipe.id)]?.allergens || recipe.allergens)
              : recipe.allergens
            return modalAllergens?.length > 0 ? (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">{trLabel(isSpanish, translations, 'Allergens')}: {modalAllergens.join(', ')}</span>
                </div>
              </div>
            ) : null
          })()}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// RECIPE EDIT MODAL
// ══════════════════════════════════════════════════════════════════════════════

function RecipeEditModal({ recipe, onClose, onSave }) {
  const { currentCity } = useApp()
  const [formData, setFormData] = useState({
    name: recipe?.name || '',
    category: recipe?.category || 'sauce',
    base_batch_size: recipe?.base_batch_size || 10,
    base_batch_unit: recipe?.base_batch_unit || 'lbs',
    ingredients: recipe?.ingredients || [],
    prep_instructions: recipe?.prep_instructions || '',
    cook_instructions: recipe?.cook_instructions || '',
    chef_notes: recipe?.chef_notes || '',
    allergens: recipe?.allergens || [],
  })
  const [saving, setSaving] = useState(false)
  const { success, error } = useToastActions()

  const isNew = !recipe?.id

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  function handleIngredientChange(index, field, value) {
    const newIngredients = [...formData.ingredients]
    newIngredients[index] = { ...newIngredients[index], [field]: value }
    setFormData(prev => ({ ...prev, ingredients: newIngredients }))
  }

  function addIngredient() {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { ingredient_name: '', quantity: 0, unit: 'lbs' }]
    }))
  }

  function removeIngredient(index) {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    try {
      setSaving(true)
      if (isNew) {
        await recipesAPI.create({ ...formData, city_id: currentCity })
        success('Recipe created')
      } else {
        await recipesAPI.update(recipe.db_id ?? recipe.id, formData)
        success('Recipe updated')
      }
      onSave()
      onClose()
    } catch (err) {
      error(isNew ? 'Failed to create recipe' : 'Failed to update recipe')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault() }} className="flex flex-col h-full">
          <div className="modal-header">
            <h3 className="font-display text-xl font-semibold text-havn-green-900">
              {isNew ? 'New Recipe' : 'Edit Recipe'}
            </h3>
            <button type="button" onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-body flex-1 overflow-y-auto space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-havn-charcoal mb-1">Recipe Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input"
                  placeholder="e.g., Creamy Sun-Dried Tomato Sauce"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-havn-charcoal mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="input"
                >
                  <option value="protein">Protein</option>
                  <option value="sauce">Sauce</option>
                  <option value="vegetable">Vegetable</option>
                  <option value="grain">Grain</option>
                  <option value="base">Base</option>
                  <option value="seasoning">Seasoning</option>
                </select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-havn-charcoal mb-1">Batch Size</label>
                  <input
                    type="number"
                    value={formData.base_batch_size}
                    onChange={(e) => handleChange('base_batch_size', parseFloat(e.target.value) || 0)}
                    className="input"
                    step="0.1"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-sm font-medium text-havn-charcoal mb-1">Unit</label>
                  <select
                    value={formData.base_batch_unit}
                    onChange={(e) => handleChange('base_batch_unit', e.target.value)}
                    className="input"
                  >
                    <option value="lbs">lbs</option>
                    <option value="oz">oz</option>
                    <option value="gallons">gallons</option>
                    <option value="liters">liters</option>
                    <option value="each">each</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-havn-charcoal">Ingredients</label>
                <button type="button" onClick={addIngredient} className="text-sm text-havn-green-700 hover:text-havn-green-600 font-medium">
                  + Add Ingredient
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.ingredients.map((ing, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={ing.ingredient_name}
                      onChange={(e) => handleIngredientChange(i, 'ingredient_name', e.target.value)}
                      className="input flex-1"
                      placeholder="Ingredient name"
                    />
                    <input
                      type="number"
                      value={ing.quantity}
                      onChange={(e) => handleIngredientChange(i, 'quantity', parseFloat(e.target.value) || 0)}
                      className="input w-24"
                      step="1"
                      placeholder="Qty"
                    />
                    <select
                      value={ing.unit}
                      onChange={(e) => handleIngredientChange(i, 'unit', e.target.value)}
                      className="input w-24"
                    >
                      <option value="lbs">lbs</option>
                      <option value="oz">oz</option>
                      <option value="cups">cups</option>
                      <option value="tbsp">tbsp</option>
                      <option value="tsp">tsp</option>
                      <option value="each">each</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeIngredient(i)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-havn-charcoal mb-1">Prep Instructions</label>
              <textarea
                value={formData.prep_instructions}
                onChange={(e) => handleChange('prep_instructions', e.target.value)}
                className="input min-h-[100px]"
                placeholder="Enter prep instructions..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-havn-charcoal mb-1">Cook Instructions</label>
              <textarea
                value={formData.cook_instructions}
                onChange={(e) => handleChange('cook_instructions', e.target.value)}
                className="input min-h-[100px]"
                placeholder="Enter cooking instructions..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-havn-charcoal mb-1">Chef's Notes</label>
              <textarea
                value={formData.chef_notes}
                onChange={(e) => handleChange('chef_notes', e.target.value)}
                className="input min-h-[80px]"
                placeholder="Tips for execution, variations, etc..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? <span className="spinner w-4 h-4" /> : <Save className="w-4 h-4" />}
              {isNew ? 'Create Recipe' : 'Update Recipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN RECIPES PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function Recipes({ chefMode = false, brothRecipeIds = [] }) {
  const { currentMenu, currentCity, isSpanish, translations } = useApp()
  const t = (label) => trLabel(isSpanish, translations, label)
  const location = useLocation()
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [editingRecipe, setEditingRecipe] = useState(null)
  const [viewingRecipe, setViewingRecipe] = useState(null)
  const [showNewModal, setShowNewModal] = useState(false)
  const [duplicatingRecipe, setDuplicatingRecipe] = useState(null)
  // Toggle for Current Menu vs All Recipes
  const [recipeScope, setRecipeScope] = useState(() => chefMode ? 'menu' : (initialParams.get('scope') === 'all' ? 'all' : 'menu')) // 'menu' or 'all'
  const [scaleResult, setScaleResult] = useState(null)
  const [scaleRetry, setScaleRetry] = useState(0)
  const recipeRequest = useRef(0)
  const scaleIsCurrent = scaleResult?.menuId === currentMenu && scaleResult?.recipes === recipes
  const scaledRecipeData = scaleIsCurrent ? scaleResult.data : {}
  const scalingError = scaleIsCurrent ? scaleResult.error : null
  const loadingScaled = !!currentMenu && recipes.length > 0 && !scaleIsCurrent
  const { success, error } = useToastActions()

  // Load recipes when scope, menu, or city changes
  useEffect(() => {
    setViewingRecipe(null)
    loadRecipes()
    return () => { recipeRequest.current += 1 }
  }, [recipeScope, currentMenu, currentCity])

  // Multi-user sync: poll for recipe changes every 60 seconds
  useEffect(() => {
    let active = true
    const intervalId = setInterval(() => {
      // Silently refresh recipes in the background
      (async () => {
        try {
          let data
          if (chefMode && !currentMenu) return
          if ((chefMode || recipeScope === 'menu') && currentMenu) {
            data = await recipesAPI.forMenu(currentMenu)
          } else {
            data = await recipesAPI.list(currentCity ? { city_id: currentCity } : {})
          }
          // Only update if count or content changed
          if (active && JSON.stringify(data) !== JSON.stringify(recipes)) {
            setRecipes(data)
          }
        } catch (err) {
          // Silently ignore polling errors
        }
      })()
    }, 60000) // 60 seconds
    return () => { active = false; clearInterval(intervalId) }
  }, [chefMode, recipeScope, currentMenu, currentCity, recipes]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tie every response to the menu and recipe list that requested it. Menu changes
  // hide the old targets immediately, and late requests cannot restore them.
  useEffect(() => {
    if (!currentMenu || !recipes.length) return
    let active = true
    orderingAPI.calculate(currentMenu)
      .then((ordering) => loadMenuRecipeScales(recipes, ordering, recipesAPI.scale))
      .then((data) => { if (active) setScaleResult({ menuId: currentMenu, recipes, data, error: null }) })
      .catch(() => { if (active) setScaleResult({ menuId: currentMenu, recipes, data: {}, error: 'Unable to calculate menu quantities.' }) })
    return () => { active = false }
  }, [currentMenu, recipes, scaleRetry])

  async function loadRecipes() {
    const request = ++recipeRequest.current
    setRecipes([])
    try {
      setLoading(true)
      if (chefMode && !currentMenu) return
      let data
      if ((chefMode || recipeScope === 'menu') && currentMenu) {
        data = await recipesAPI.forMenu(currentMenu)
      } else {
        data = await recipesAPI.list(currentCity ? { city_id: currentCity } : {})
      }
      if (request === recipeRequest.current) setRecipes(data)
    } catch (err) {
      if (request === recipeRequest.current) error('Failed to load recipes')
    } finally {
      if (request === recipeRequest.current) setLoading(false)
    }
  }

  async function handleDelete(recipe) {
    if (!confirm(`Delete "${recipe.name}"? This cannot be undone.`)) return

    try {
      await recipesAPI.delete(recipe.db_id ?? recipe.id)
      setRecipes(prev => prev.filter(r => r.id !== recipe.id))
      success('Recipe deleted')
    } catch (err) {
      error('Failed to delete recipe')
    }
  }

  function handleDuplicate(recipe) {
    // Deep-clone the recipe data, strip IDs so it's treated as "new"
    const cloned = {
      name: `${recipe.name} (Copy)`,
      category: recipe.category,
      base_batch_size: recipe.base_batch_size,
      base_batch_unit: recipe.base_batch_unit,
      ingredients: recipe.ingredients?.map(ing => ({ ...ing })) || [],
      prep_instructions: recipe.prep_instructions || '',
      cook_instructions: recipe.cook_instructions || '',
      chef_notes: recipe.chef_notes || '',
      allergens: [...(recipe.allergens || [])],
    }
    // No id → RecipeEditModal will treat this as isNew = true
    setDuplicatingRecipe(cloned)
  }

  function handlePrint(recipe, referenceTarget = null) {
    if (!chefMode && recipeScope === 'all' && Number(referenceTarget) > 0) {
      exportAPI.openRecipeCard(recipe.db_id ?? recipe.id, Number(referenceTarget), isSpanish ? 'spanish' : null)
      return
    }
    const scaled = scaledRecipeData[recipe.db_id]
    if (!scaled?.scaled_batch_size) {
      error('This recipe is not used by the current menu, so there is no production batch to print.')
      return
    }
    exportAPI.openRecipeCard(recipe.db_id ?? recipe.id, scaled.scaled_batch_size, isSpanish ? 'spanish' : null, currentMenu)
    success('Opening print view...')
  }

  // Filter recipes
  const filteredRecipes = useMemo(() => {
    return recipes.filter(recipe => {
      const matchesSearch = !searchTerm ||
        recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipe.ingredients?.some(i => i.ingredient_name?.toLowerCase().includes(searchTerm.toLowerCase()))
      const matchesCategory = !categoryFilter || recipe.category === categoryFilter
      return matchesSearch && matchesCategory
    })
  }, [recipes, searchTerm, categoryFilter])

  // Get unique categories
  const categories = useMemo(() => {
    return [...new Set(recipes.map(r => r.category))].sort()
  }, [recipes])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-havn-green-700" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      {chefMode && (
        <div className="mb-5 rounded-xl border border-havn-brass-200 bg-havn-brass-50 px-5 py-4">
          <p className="text-xs uppercase tracking-[0.16em] text-havn-brass-700 font-medium">Chef workspace</p>
          <h1 className="font-display text-2xl text-havn-green-900 mt-1">{t('Current-menu recipes')}</h1>
          <p className="text-sm text-havn-gray-600 mt-1">{t('All recipes for this menu, grouped by category. Open a card to view, print, or edit the scaled recipe.')}</p>
        </div>
      )}
      {scalingError && <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">
        {scalingError} <button className="underline ml-2" onClick={() => { setScaleResult(null); setScaleRetry((value) => value + 1) }}>Retry</button>
      </div>}
      {/* Recipe scope. Production quantities are always menu-scaled. */}
      {currentMenu && (
        <div className="flex items-center justify-center gap-6 mb-6 p-3 bg-havn-cream-100 rounded-lg">
          {/* Scope toggle: Current Menu vs All */}
          {!chefMode && <div className="flex items-center gap-2">
            <span className="text-sm text-havn-gray-600">Show:</span>
            <button
              onClick={() => setRecipeScope('menu')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                recipeScope === 'menu'
                  ? 'bg-havn-green-700 text-white'
                  : 'bg-white text-havn-gray-600 hover:bg-havn-cream-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Current Menu
            </button>
            <button
              onClick={() => setRecipeScope('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                recipeScope === 'all'
                  ? 'bg-havn-green-700 text-white'
                  : 'bg-white text-havn-gray-600 hover:bg-havn-cream-200'
              }`}
            >
              <Globe className="w-4 h-4" />
              All Recipes
            </button>
          </div>}

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-havn-green-700 text-white">
            <Scale className="w-4 h-4" />
            {!chefMode && recipeScope === 'all' ? 'Recipe library · reference batches' : 'Menu-scaled automatically'}
            {loadingScaled && (chefMode || recipeScope === 'menu') && <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
          </div>
        </div>
      )}

      {currentMenu && (chefMode || recipeScope === 'menu') && (
        <div className="mb-6 p-4 bg-havn-brass-50 border border-havn-brass-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Scale className="w-5 h-5 text-havn-brass-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-havn-charcoal">Scaled View</h4>
              <p className="text-sm text-havn-gray-600 mt-1">
                Showing recipe quantities scaled to match the current menu's meal counts.
                Click a recipe to see the detailed scaled ingredients.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-havn-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search recipes or ingredients..."
              className="input pl-10"
            />
          </div>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="input w-40"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {!chefMode && <div className="flex items-center gap-2">
          {currentMenu && (
            <button
              onClick={() => exportAPI.openAllRecipes(currentMenu, isSpanish ? 'spanish' : null)}
              className="btn-secondary"
            >
              <FileText className="w-4 h-4" />
              Print current menu
            </button>
          )}
          {currentMenu && (
            <button
              onClick={async () => {
                try {
                  const menuRecipes = await recipesAPI.forMenu(currentMenu)
                  const menu = await menusAPI.get(currentMenu)
                  const ordering = await orderingAPI.calculate(currentMenu)
                  const scaledMap = await loadMenuRecipeScales(menuRecipes, ordering, recipesAPI.scale)
                  const md = buildAllRecipesMD(menuRecipes || [], menu?.name, currentCity, scaledMap)
                  downloadMD(
                    buildFilename('recipes-bulk-scaled', { city: currentCity, menu: menu?.name }),
                    md,
                  )
                  success(`Exported ${(menuRecipes || []).length} recipes for AI (scaled to menu)`)
                } catch (err) {
                  error('Failed to export recipes: ' + (err.message || 'Unknown error'))
                }
              }}
              className="btn-secondary"
              title="Download all menu recipes as one markdown file for an LLM"
            >
              <FileCode className="w-4 h-4" />
              Export current menu for AI
            </button>
          )}
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            New Recipe
          </button>
        </div>}
      </div>

      {/* Recipe Stats */}
      <div className="flex items-center gap-4 mb-6 text-sm text-havn-gray-500">
        <span>{filteredRecipes.length} recipes</span>
        {searchTerm && <span>matching "{searchTerm}"</span>}
        {categoryFilter && <span>in {categoryFilter}</span>}
      </div>

      {/* Recipe Grid */}
      {filteredRecipes.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <BookOpen className="w-12 h-12 text-havn-gray-300 mb-4" />
          <h3 className="font-display text-lg font-semibold text-havn-gray-500">
            {searchTerm || categoryFilter ? 'No matching recipes' : 'No recipes yet'}
          </h3>
          <p className="text-sm text-havn-gray-400 mt-1">
            {searchTerm || categoryFilter
              ? 'Try adjusting your search or filters'
              : 'Create your first recipe to get started'}
          </p>
          {!chefMode && !searchTerm && !categoryFilter && (
            <button onClick={() => setShowNewModal(true)} className="btn-primary mt-4">
              <Plus className="w-4 h-4" />
              Create Recipe
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {(chefMode ? groupChefRecipes(filteredRecipes, brothRecipeIds) : [{ key: 'all', items: filteredRecipes }]).map((group) => <section key={group.key}>
            {chefMode && <h2 className="font-display text-xl text-havn-green-900 mb-3">{t(group.label)} <span className="text-sm text-havn-gray-500">({group.items.length})</span></h2>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.items.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  scaledData={!chefMode && recipeScope === 'all' ? null : scaledRecipeData[recipe.db_id]}
                  referenceMode={!chefMode && recipeScope === 'all'}
                  scalingComplete={!loadingScaled}
                  scalingError={scalingError}
                  onEdit={() => setEditingRecipe(recipe)}
                  onDelete={() => handleDelete(recipe)}
                  onView={() => setViewingRecipe(recipe)}
                  onDuplicate={() => handleDuplicate(recipe)}
                  chefMode={chefMode}
                />
              ))}
            </div>
          </section>)}
        </div>
      )}

      {/* Modals */}
      {(editingRecipe || showNewModal || duplicatingRecipe) && (
        <RecipeEditModal
          recipe={editingRecipe || duplicatingRecipe}
          onClose={() => { setEditingRecipe(null); setShowNewModal(false); setDuplicatingRecipe(null) }}
          onSave={loadRecipes}
        />
      )}

      {viewingRecipe && (
        <RecipeDetailModal
          recipe={viewingRecipe}
          scaledData={scaledRecipeData[viewingRecipe.db_id]}
          referenceMode={!chefMode && recipeScope === 'all'}
          scalingComplete={!loadingScaled}
          scalingError={scalingError}
          onClose={() => setViewingRecipe(null)}
          onPrint={(referenceTarget) => handlePrint(viewingRecipe, referenceTarget)}
          onEdit={() => { loadRecipes(); setViewingRecipe(null) }}
        />
      )}
    </div>
  )
}
